import React, { useEffect, useState } from 'react';
import {
  Box, Radio, RadioGroup, FormControlLabel, Typography, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { toast } from 'react-toastify';
import Modal from '../Modal/Modal';
import Button from '../Button';
import GhostButton from '../GhostButton';
import { couponAPI } from '../../api/services/userApi';
import type { FirstPromoterCampaignType } from '../../types/firstPromoterCampaignTypes';
import type { CouponType } from '../../types/couponTypes';

type ModeType = 'unconfigured' | 'free' | 'coupon';

type PropsType = {
  campaign: FirstPromoterCampaignType;
  onCloseModal: () => void;
  onSaveFree: () => void;
  onSaveReset: () => void;
  onSaveExistingCoupon: (coupon: CouponType) => void;
};

const getInitialMode = (campaign: FirstPromoterCampaignType): ModeType => {
  if (campaign.isSubscriptionFree) return 'free';
  if (campaign.stripePromotionCodeId) return 'coupon';
  return 'unconfigured';
};

// First step of editing a campaign: choose how it should be configured. "Coupon" mode only picks
// from already-existing Stripe promotion codes (created on the general Coupon Codes admin page) -
// no new coupon can be created from here.
const EditFirstPromoterCampaignModal: React.FC<PropsType> = (props) => {
  const [mode, setMode] = useState<ModeType>(getInitialMode(props.campaign));
  const [existingCoupons, setExistingCoupons] = useState<CouponType[]>([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [selectedPromotionCodeId, setSelectedPromotionCodeId] = useState<string>(
    props.campaign.stripePromotionCodeId ?? '',
  );

  useEffect(() => {
    if (mode !== 'coupon' || existingCoupons.length > 0) return;
    (async () => {
      setIsLoadingCoupons(true);
      try {
        const res = await couponAPI.getCoupons({
          findString: '',
          startDate: null,
          endDate: null,
          page: '1',
          perPage: '100',
          status: '',
        });
        setExistingCoupons(res.data.data?.coupons ?? []);
      } catch (err) {
        toast.error('Failed to load existing Stripe coupons');
      } finally {
        setIsLoadingCoupons(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const onSave = () => {
    if (mode === 'free') {
      props.onSaveFree();
    } else if (mode === 'coupon') {
      const selected = existingCoupons.find((c) => c.id === selectedPromotionCodeId);
      if (selected) props.onSaveExistingCoupon(selected);
    } else {
      props.onSaveReset();
    }
  };

  const isSaveDisabled = mode === 'coupon' && !selectedPromotionCodeId;

  return (
    <Modal>
      <Box className="styled-modal__container__form__input">
        <h3>{`Configure "${props.campaign.name}"`}</h3>
      </Box>
      {props.campaign.firstPromoterRewardCoupon && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          FirstPromoter&apos;s own referral reward for this campaign:
          {' '}
          <strong>{props.campaign.firstPromoterRewardCoupon}</strong>
          {' '}
          (shown for reference only - not applied automatically)
        </Typography>
      )}
      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as ModeType)}>
        <FormControlLabel value="unconfigured" control={<Radio />} label="Unconfigured (no discount, no free access)" />
        <FormControlLabel value="free" control={<Radio />} label="Subscription free - signups from this campaign get a free account" />
        <FormControlLabel value="coupon" control={<Radio />} label="Attach a Stripe coupon - auto-applied at checkout" />
      </RadioGroup>
      {mode === 'coupon' && (
        <Box sx={{ mt: 1 }}>
          <FormControl fullWidth disabled={isLoadingCoupons}>
            <InputLabel id="existing-coupon-label" shrink>
              {isLoadingCoupons ? 'Loading coupons...' : 'Coupon'}
            </InputLabel>
            <Select
              fullWidth
              displayEmpty
              labelId="existing-coupon-label"
              label={isLoadingCoupons ? 'Loading coupons...' : 'Coupon'}
              value={selectedPromotionCodeId}
              onChange={(e) => setSelectedPromotionCodeId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a coupon...</em>
              </MenuItem>
              {existingCoupons.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {`${c.code} (${c.discount}% off, ${c.duration})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!isLoadingCoupons && existingCoupons.length === 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              No Stripe coupons exist yet - create one on the Coupon Codes admin page first.
            </Typography>
          )}
        </Box>
      )}
      <Box className="styled-modal__container__buttons-area" mt={3}>
        <GhostButton onClick={props.onCloseModal}>
          <p className="text-3">Cancel</p>
        </GhostButton>
        <Button onClick={onSave} disabled={isSaveDisabled}>
          <h4>Save</h4>
        </Button>
      </Box>
    </Modal>
  );
};

export default EditFirstPromoterCampaignModal;
